import { useRouter } from 'next/router'
import { checkIsServiceNonTopUp, checkIsWithPercentFee, PaymentPayee, useCyberStore } from '@entities/tsp'
import { getResultPath, TspPaymentStep } from '@entities/payment'
import { useBindingsStore } from '@entities/user'
import { generatePath, parseAmount } from '@shared/lib'
import { CYBER_PAY_URL_RESULT, ewalletReqHeaders } from '@shared/config'
import { PayeeBindingTypesEnum } from '@root/api/ump-catalog-manager'
import { TransactionResponse } from '@root/api/ump-ewallet'
import { useToast } from '@shared/lib/toast'
import { getConfirmationContent } from '../lib/getConfirmationContent'
import { usePaymentDo } from './usePaymentDo'
import { TRANSACTION_STATE } from '../config/transactionStates'

interface UsePayByMtsAccountArgs {
  isVouchers: boolean
  payee: PaymentPayee
}

export const usePayByMtsAccount = ({ isVouchers, payee }: UsePayByMtsAccountArgs) => {
  const router = useRouter()

  const { toast } = useToast()

  const { setStep, pcekMdOrder, amountInfo } = useCyberStore()
  const { bindings } = useBindingsStore()

  const { paymentDo, isLoading: isPaymentDoLoading } = usePaymentDo()

  const url = router.query.id as string

  const mtsBinding = bindings?.find((item) => item.bindingType === PayeeBindingTypesEnum.MTSACCOUNT)

  const payByMtsAccount = () => {
    const totalAmount = amountInfo?.total

    const onError = (error) => toast({ variant: 'error', description: error?.message || '' })

    const isNonTopUpService = checkIsServiceNonTopUp(isVouchers, payee)
    const isTopUpWithPercentFeeService = checkIsWithPercentFee(payee)

    const redirectToResult = (mdOrder: string) => {
      const generatedPath = `${generatePath(CYBER_PAY_URL_RESULT, {
        mdOrder,
      })}?url=${url}&voucher=${isNonTopUpService}&topup=${isTopUpWithPercentFeeService}`

      router.push(generatedPath)
    }

    const handleCallOtp = async () => {
      const isFromPubgGiveawayPage = router.query.from === 'rozygrysh-prizov-pubg-mobile'

      await router.push(`${router.asPath}${isFromPubgGiveawayPage ? '?from=rozygrysh-prizov-pubg-mobile' : ''}#otp`)
      setStep(TspPaymentStep.PAYMENT_OTP)
    }

    const handleCallError = (errorMessage?: string) => {
      toast({ variant: 'error', title: 'Ошибка платежа', description: errorMessage || '' })
    }

    const onSuccess = (data: TransactionResponse) => {
      if (data.state === TRANSACTION_STATE.DEPOSITED || data.state === TRANSACTION_STATE.IN_PROGRESS) {
        redirectToResult(data?.mdOrder as string)
      } else if (data.state === TRANSACTION_STATE.WAITING_CONFIRMATION) {
        getConfirmationContent({
          paymentData: data,
          getResultPath: () =>
            getResultPath(
              data?.mdOrder as string,
              `?url=${url}&voucher=${isNonTopUpService}&topup=${isTopUpWithPercentFeeService}`
            ),
          handleCallOtp,
          handleError: handleCallError,
        })
      }
    }

    if ((mtsBinding?.balance as number) < parseAmount(totalAmount as string)) {
      toast({ variant: 'error', description: 'Недостаточно средств' })
    } else {
      paymentDo({
        mdOrder: pcekMdOrder,
        onError,
        onSuccess,
        options: {
          headers: {
            ...ewalletReqHeaders,
          },
        },
      })
    }
  }

  return { payByMtsAccount, isPaymentByMtsAccountLoading: isPaymentDoLoading }
}
